import { Menu as MenuIcon, PanelLeftClose, PanelLeftOpen, X } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cx } from '../../internal/cx.ts';
import { useEscape } from '../../internal/events.ts';
import { useFocusTrap, useScrollLock } from '../../internal/focus.ts';
import { useIsPhone, useMediaQuery } from '@pidelo/web-core';
import { Avatar } from '../Avatar/Avatar.tsx';
import { isPathActive, type ShellLink, type ShellNavGroup } from './nav.ts';
import styles from './AppShell.module.css';

export type ShellState = { collapsed: boolean; phone: boolean };

export type AppShellProps = {
  /** The logo with its caption, for the open menu (dark background). */
  brand: ReactNode;
  /** The symbol alone, for the narrow menu. */
  brandCompact: ReactNode;
  nav: readonly ShellNavGroup[];
  navSearch?: ReactNode;
  currentPath: string;
  LinkComponent: ShellLink;
  /** The section title, in the phone top bar. */
  title: string;
  /** Right side of the phone top bar (the shift chip). */
  topBarStatus?: ReactNode;
  /** Above the person's card: shift, live indicator. */
  sidebarExtra?: ReactNode | ((state: ShellState) => ReactNode);
  user?: { name: string; detail?: ReactNode; onClick: () => void; label?: string };
  /** Notices above every page (no connection, shift needed). */
  banner?: ReactNode;
  /** Where the narrow-menu choice is remembered. */
  storageKey?: string;
  children: ReactNode;
};

function readCollapsed(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

/**
 * The admin frame: a dark menu on the left with the sections the person may see, narrow (icons only)
 * on tablets or when chosen, and a drawer with a top bar on the phone.
 */
export function AppShell({ brand, brandCompact, nav, navSearch, currentPath, LinkComponent, title, topBarStatus, sidebarExtra, user, banner, storageKey = 'pidelo.compact-navigation', children }: AppShellProps) {
  const phone = useIsPhone();
  const tablet = useMediaQuery('(min-width: 720px) and (max-width: 1099px)');
  const [chosenNarrow, setChosenNarrow] = useState(() => readCollapsed(storageKey));
  const [drawer, setDrawer] = useState(false);
  const sidebar = useRef<HTMLElement>(null);
  const menuButton = useRef<HTMLButtonElement>(null);
  const collapsed = !phone && (tablet || chosenNarrow);
  const drawerOpen = phone && drawer;

  useFocusTrap(sidebar, drawerOpen);
  useScrollLock(drawerOpen);
  useEscape(() => {
    setDrawer(false);
    menuButton.current?.focus();
  }, drawerOpen);

  useEffect(() => setDrawer(false), [currentPath, phone]);

  const toggleNarrow = () => {
    const next = !chosenNarrow;
    setChosenNarrow(next);
    try {
      localStorage.setItem(storageKey, next ? '1' : '0');
    } catch {
      // The choice just will not be remembered.
    }
  };

  const extra = typeof sidebarExtra === 'function' ? sidebarExtra({ collapsed, phone }) : sidebarExtra;

  return (
    <div className={styles.shell} data-collapsed={collapsed ? '' : undefined}>
      <a href="#contenido" className={styles.skip}>
        Ir al contenido
      </a>
      {phone ? (
        <header className={styles.topBar}>
          <button ref={menuButton} type="button" className={styles.topButton} aria-label="Abrir el menú" aria-expanded={drawerOpen} aria-controls="menu-principal" onClick={() => setDrawer(true)}>
            <MenuIcon aria-hidden="true" />
          </button>
          <span className={styles.topBrand}>{brandCompact}</span>
          <b className={styles.topTitle}>{title}</b>
          {topBarStatus}
        </header>
      ) : null}
      {drawerOpen ? <div className={styles.scrim} onClick={() => setDrawer(false)} aria-hidden="true" /> : null}
      <nav
        ref={sidebar}
        id="menu-principal"
        className={styles.sidebar}
        data-open={drawerOpen ? '' : undefined}
        aria-label="Menú principal"
        {...(phone && !drawerOpen ? { inert: true } : {})}
        {...(drawerOpen ? { role: 'dialog', 'aria-modal': true } : {})}
      >
        <div className={styles.brandRow}>
          <span className={styles.brand}>{collapsed ? brandCompact : brand}</span>
          {phone ? (
            <button type="button" className={styles.close} aria-label="Cerrar el menú" onClick={() => setDrawer(false)} data-autofocus="">
              <X aria-hidden="true" />
            </button>
          ) : null}
        </div>
        {navSearch && !collapsed ? <div className={styles.navSearch}>{navSearch}</div> : null}
        <div className={styles.groups}>
          {nav.map((group, index) => (
            <div key={group.label ?? index} className={styles.group} role="group" aria-label={group.label}>
              {group.label && !collapsed ? <p className={styles.groupLabel}>{group.label}</p> : null}
              {group.items
                .filter(item => item.only !== (phone ? 'wide' : 'phone'))
                .map(item => {
                  const active = isPathActive(currentPath, item.to, item.end);
                  return (
                    <LinkComponent key={item.to} to={item.to} className={cx(styles.item)} aria-current={active ? 'page' : undefined} title={collapsed ? item.label : undefined}>
                      <span className={styles.itemIcon} aria-hidden="true">
                        {item.icon}
                      </span>
                      <span className={styles.itemLabel}>{item.label}</span>
                      {item.badge ? (
                        <b className={styles.itemBadge} aria-label={`${item.badge} pendientes`}>
                          {item.badge > 99 ? '99+' : item.badge}
                        </b>
                      ) : null}
                    </LinkComponent>
                  );
                })}
            </div>
          ))}
        </div>
        {extra ? <div className={styles.extra}>{extra}</div> : null}
        {user ? (
          <button type="button" className={styles.user} onClick={user.onClick} title={collapsed ? user.name : undefined} aria-label={user.label ?? `Tu cuenta: ${user.name}`}>
            <Avatar name={user.name} size="sm" />
            <span className={styles.userText}>
              <b>{user.name}</b>
              {user.detail ? <small>{user.detail}</small> : null}
            </span>
          </button>
        ) : null}
        {!phone && !tablet ? (
          <button type="button" className={styles.narrow} onClick={toggleNarrow} aria-pressed={chosenNarrow} title={chosenNarrow ? 'Abrir el menú completo' : 'Achicar el menú'}>
            {chosenNarrow ? <PanelLeftOpen aria-hidden="true" /> : <PanelLeftClose aria-hidden="true" />}
            <span className={styles.itemLabel}>{chosenNarrow ? 'Abrir el menú completo' : 'Achicar el menú'}</span>
          </button>
        ) : null}
      </nav>
      <div className={styles.main}>
        {banner ? <div className={styles.banner}>{banner}</div> : null}
        <main id="contenido" className={styles.content} tabIndex={-1}>
          {children}
        </main>
      </div>
    </div>
  );
}
